/* eslint-disable @typescript-eslint/naming-convention */
import React, { useEffect, useMemo } from 'react'
import { maxlength, minlength, required, link, regex } from 'validation-rules'
import { makeStyles } from '@material-ui/core'
import { getValue } from 'remote-config-value'
import useFlatForm from '../../../hooks/useFlatForm'
import InputWithExplanation from './InputWithExplanation'
import FileUploader, { EncodedFile } from './FileUploader'
import EmphasizedSection from '../../shared/EmphasizedSection'
import { Select, Typography, RequiredAsterisk } from 'foundations-library/components'
import { isMarketingUsecasePresent as isMarketingUsecasePresentUtil, replacePlaceholderWithActualBrandValues } from '../../../utils/util'

const MESSAGE_FLOW_MIN_LENGTH = 40
const MESSAGE_FLOW_MAX_LENGTH = 4096
const verbalOptionMarketingRegex = /written consent/i

const jss = (theme) => {
    const { colors } = theme['foundations-library']

    return {
        flex20: {
            display: 'flex',
            flexDirection: 'column',
            gap: 20
        },
        inputLabel: {
            marginBottom: 15
        },
        mb10: {
            marginBottom: 10
        },
        mb30: {
            marginBottom: 30
        },
        textarea: {
            marginBottom: 20,
            '& textarea': {
                minHeight: 150
            }
        },
        inputContainer: {
            display: 'flex',
            flexDirection: 'column',
            gap: 30
        },
        websiteConsentSample: {
            background: colors.neutral[100],
            borderRadius: 6,
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            marginTop: 10,
            padding: 15,
            wordBreak: 'break-word',
            marginLeft: -30
        },
        websiteConsentSampleTitle: {
            margin: 0
        },
        websiteConsentSampleText: {
            margin: 0
        }
    }
}

const useStyles = makeStyles(jss)

interface PropsInterface {
    disabled: boolean
    initialValue: {
        messageFlow: string
        consentInfo: {
            initialContact: string
            pointOfVerbalContact: string
            publiclyAccessibleUrl: string
            isRequirementsConsentAcknowledged: boolean
        }
        files: EncodedFile[]
    },
    onChange(v): unknown
    isValid(obj: Record<string, boolean>): unknown
    item?: Record<string, unknown>,
    useCases: string[],
    brand?: Record<string, unknown>
}

const verbalOptions = {
    'phone-call': {
        text: 'Phone call',
        label: getValue('sms_enrollment_campaigns_1st_step_describe_how_obtein_consent_over_phone'),
        info: getValue('sms_enrollment_campaigns_1st_step_verbal_phone_sample_description')
    },
    'web-meeting': {
        text: 'Web meeting',
        label: getValue('sms_enrollment_campaigns_1st_step_describe_how_obtein_consent_during_web_meeting'),
        info: getValue('sms_enrollment_campaigns_1st_step_web_meeting_sample_description')
    },
    'in-person': {
        text: 'In-person conversation',
        label: getValue('sms_enrollment_campaigns_1st_step_describe_how_obtein_consent_in_person'),
        info: getValue('sms_enrollment_campaigns_1st_step_in_person_sample_description')
    }
}

const getMessageFlowValue = (userInput, cInfo, brand, useCases) => {
    if (cInfo.initialContact === 'Website form') {
        const defaultValue = replacePlaceholderWithActualBrandValues(
            getValue('sms_enrollment_message_flow_value_if_website_form_selected'),
            brand,
            useCases
        )
        return defaultValue
            .replace('[PUBLICLY_ACCESSIBLE_URL]', cInfo?.publiclyAccessibleUrl)
    }

    return userInput
}

/**
 *
 */
const MessageFlow = ({
    onChange,
    isValid,
    disabled,
    initialValue,
    item,
    useCases,
    brand
}: PropsInterface): JSX.Element => {
    const styles = useStyles()

    const {
        data,
        onClear,
        setDataProp,
        setIsValidProp
    } = useFlatForm({ ...initialValue })

    const {
        data: cInfoData,
        onClear: cInfoOnClear,
        setDataProp: cInfoSetDataProp
    } = useFlatForm({ ...initialValue.consentInfo })

    const isMarketingUsecasePresent = useMemo(() => isMarketingUsecasePresentUtil(useCases), [useCases])

    const messageSample = useMemo(
        () => {
            let value = ''
            if (cInfoData.initialContact === 'Verbally') {
                value = verbalOptions[cInfoData?.pointOfVerbalContact]?.info || ''
                if (isMarketingUsecasePresent) {
                    value = `
                        ${value}
                        <br/>
                        <br/>
                        ${getValue('sms_enrollment_campaigns_1st_step_marketing_sample_description')}
                    `
                }
            } else if (cInfoData.initialContact === 'Email') {
                value = getValue('sms_enrollment_campaigns_1st_step_email_sample_description')
            } else if (cInfoData.initialContact === 'Website form') {
                value = getValue('sms_enrollment_campaigns_1st_step_website_sample_description')
            } else if (cInfoData.initialContact === 'Paper/electronic form') {
                value = getValue('sms_enrollment_campaigns_1st_step_paper_sample_description')
            }
            // else if (cInfoData.initialContact === 'Other') {
            //     value = getValue('sms_enrollment_campaigns_1st_step_paper_sample_description')
            // }
            if (!value) return ''

            return replacePlaceholderWithActualBrandValues(
                value,
                brand,
                useCases
            )
        },
        [
            isMarketingUsecasePresent,
            useCases,
            verbalOptions,
            cInfoData.initialContact,
            cInfoData?.pointOfVerbalContact,
            brand?.name
        ])

    useEffect(() => {
        onChange({
            ...data,
            messageFlow: getMessageFlowValue(data.messageFlow, cInfoData, brand, useCases),
            consentInfo: cInfoData
        })
        const consentInfoValidation = {
            initialContact: !!cInfoData.initialContact,
            pointOfVerbalContact: cInfoData.initialContact === 'Verbally'
                ? !!cInfoData.pointOfVerbalContact
                : true,
            publiclyAccessibleUrl: cInfoData.initialContact === 'Website form'
                ? !!cInfoData.publiclyAccessibleUrl
                : true,
            isRequirementsConsentAcknowledged: Boolean(cInfoData.isRequirementsConsentAcknowledged)
        }

        let isMessageFlowValid =
            !!data.messageFlow &&
            data.messageFlow?.length &&
            data.messageFlow.length >= MESSAGE_FLOW_MIN_LENGTH &&
            data.messageFlow.length <= MESSAGE_FLOW_MAX_LENGTH

        if (cInfoData.initialContact === 'Verbally' && isMarketingUsecasePresent) {
            isMessageFlowValid = isMessageFlowValid && verbalOptionMarketingRegex.test(data.messageFlow)
        }

        isValid({
            messageFlow: cInfoData.initialContact !== 'Website form' ? isMessageFlowValid : true,
            files: cInfoData.initialContact === 'Paper/electronic form' ? !!data?.files?.length : true,
            consentInfo: Object.values(consentInfoValidation).every((x) => x)
        })
    }, [data, cInfoData, isMarketingUsecasePresent])

    const obteinConsentOptions = [
        'Verbally',
        'Email',
        'Website form',
        'Paper/electronic form',
        'Other'
    ]

    const messageFlowProps = {
        // if updating rules, check for
        // isValid({
        //      messageFlow'
        // where it has to be updated. I don't know why it doesn't take this
        // functionality into consideration
        rules: [required(), minlength(MESSAGE_FLOW_MIN_LENGTH), maxlength(MESSAGE_FLOW_MAX_LENGTH)],
        placeholder: 'Enter text here',
        isValid: setIsValidProp('messageFlow'),
        onClear: onClear('messageFlow'),
        value: data.messageFlow,
        onChange: setDataProp('messageFlow'),
        disabled: disabled,
        className: styles.textarea,
        'data-testid': 'essage-flow',
        isRequirementsConsentAcknowledged: cInfoData.isRequirementsConsentAcknowledged,
        onIsRequirementsConsentAcknowledgedChange: cInfoSetDataProp('isRequirementsConsentAcknowledged')
    }

    return <EmphasizedSection className={styles.flex20} data-testid='flow'>
        <div>
            <Typography variant='body1' className={styles.inputLabel} data-testid='flow-infolabel'>
                {getValue('sms_enrollment_campaigns_1st_step_how_to_obtain_customer_consent')}<RequiredAsterisk />
            </Typography>
            <Select
                options={obteinConsentOptions.map((x) => ({ text: x, value: x }))}
                value={cInfoData.initialContact}
                onChange={cInfoSetDataProp('initialContact')}
                onClear={cInfoOnClear('initialContact')}
                rules={[required()]}
                disabled={disabled}
                data-testid='initial-contact'
            />
        </div>
        {
            cInfoData.initialContact === 'Verbally' &&
            <div className={styles.flex20}>
                <div>
                    <Typography variant='body1' className={styles.inputLabel} data-testid='flow-verbally'>
                        {getValue('sms_enrollment_campaigns_1st_step_whats_the_point_of_verbal_contact')}<RequiredAsterisk />
                    </Typography>
                    <Select
                        options={Object.entries(verbalOptions).map(([key, value]) => ({ text: value.text, value: key }))}
                        value={cInfoData.pointOfVerbalContact}
                        onChange={cInfoSetDataProp('pointOfVerbalContact')}
                        onClear={cInfoOnClear('pointOfVerbalContact')}
                        rules={[required()]}
                        disabled={disabled}
                        data-testid='flow-verbally-select3'
                    />
                </div>
                {
                    cInfoData.pointOfVerbalContact &&
                    <InputWithExplanation
                        {...messageFlowProps}
                        rules={
                            isMarketingUsecasePresent
                                ? [
                                    ...messageFlowProps.rules,
                                    regex(
                                        verbalOptionMarketingRegex,
                                        null,
                                        'Marketing use case selected. Written consent is also required'
                                    )
                                ]
                                : messageFlowProps.rules
                        }
                        label={verbalOptions[cInfoData.pointOfVerbalContact].label}
                        sample={messageSample}
                        sampleTitle='Approved verbal consent:'
                        requirements={getValue('sms_enrollment_campaigns_1st_step_verbal_requirements')}
                    />
                }
            </div>
        }
        {
            cInfoData.initialContact === 'Email' &&
            <InputWithExplanation
                {...messageFlowProps}
                label={getValue('sms_enrollment_campaigns_1st_step_whats_the_point_of_email_contanct')}
                sample={messageSample}
                placeholder='Enter text here'
                sampleTitle='Approved email consent:'
                requirements={getValue('sms_enrollment_campaigns_1st_step_email_requirements')}
            />
        }
        {
            cInfoData.initialContact === 'Website form' &&
            <InputWithExplanation
                type='url'
                inputType='TextField'
                placeholder='Enter URL...'
                rules={[required(), link()]}
                disabled={disabled}
                value={cInfoData.publiclyAccessibleUrl}
                onChange={cInfoSetDataProp('publiclyAccessibleUrl')}
                onClear={cInfoOnClear('publiclyAccessibleUrl')}
                label={getValue('sms_enrollment_campaigns_1st_step_website_form_url')}
                requirements={getValue('sms_enrollment_campaigns_1st_step_website_requirements')}
                data-testid='flow-website-textfield'
                sample={messageSample}
                sampleTitle='Approved website consent:'
                isRequirementsConsentAcknowledged={cInfoData.isRequirementsConsentAcknowledged}
                onIsRequirementsConsentAcknowledgedChange={cInfoSetDataProp('isRequirementsConsentAcknowledged')}
            />
        }
        {
            cInfoData.initialContact === 'Paper/electronic form' &&
            <div className={styles.inputContainer}>
                <div>
                    <FileUploader
                        value={data.files}
                        onChange={setDataProp('files')}
                        disabled={disabled}
                        item={item}
                    />
                </div>
                <InputWithExplanation
                    {...messageFlowProps}
                    label={getValue('sms_enrollment_campaigns_1st_step_paper_describe_how_to')}
                    sample={messageSample}
                    sampleTitle='Approved paper/electronic consent:'
                    requirements={getValue('sms_enrollment_campaigns_1st_step_file_requirements')}
                />
            </div>
        }
        {
            cInfoData.initialContact === 'Other' &&
            <div className={styles.inputContainer}>
                <InputWithExplanation
                    {...messageFlowProps}
                    label={<span>
                        Please review the consent guidelines to ensure that you meet carrier requirements. Then describe in detail how you get customer consent before messaging them.
                    </span>}
                    sample={messageSample}
                    sampleTitle='Sample SMS privacy policy:'
                    requirements={getValue('sms_enrollment_campaigns_1st_step_other_requirements')}
                />
            </div>
        }
    </EmphasizedSection >
}

export default MessageFlow
